// raisedbeef.com/membership — the landing page (owner, 2026-10-25, from a mock:
// "This looks far better than what we have" → "Build it exactly").
//
// It sells before it asks. Top to bottom:
//   1. the hero — a photograph of cattle, "The Entire Cattle Market. One
//      Membership.", one line of proof, one gold button, "No credit card
//      required. Cancel anytime."
//   2. the stat band — the barns live (the real count, src/lib/memberProof.ts),
//      the indexes, the tools, nationwide
//   3. "One plan. All the tools." — four columns
//   4. the price card — 30 DAYS FREE, $29.99 a month (a quiet yearly switch),
//      the same button
//   5. the footer.
// Both buttons go to /join, the one sign-up page (owner, 2026-10-25: "a
// dedicated signup page … do not create a second registration system"). No
// form lives here. At a gated tool the button carries ?next= so the person
// lands back on the tool.
//
// Signed in, the hero's button and the join card step aside and the price card
// becomes the account page's own billing card (days left + Subscribe, Subscribe,
// or Manage billing — SiteBilling), so this page can never offer something the
// account page does not.
//
// It is also the wall a stranger meets at a gated tool (RequireAccount passes
// `tool`): the hero's eyebrow names the tool. The masthead's Join and Sign in
// land here, and /account signed out sends people here. One door.

import { useState } from "react";

import { useSiteAccess } from "@/hooks/useSiteAccess";
import { barnCountLabel, type PaywallProof } from "@/lib/memberProof";
import { trialLine } from "@/lib/siteAccess";
import { SITE_PLAN } from "@/lib/sitePlan";

import { TickerShell } from "../cattle-ticker/TickerShell";
import { useMemberProof } from "./MemberPitch";
import SiteBilling from "./SiteBilling";

import "../rbnext.css";
import "../cattle-ticker/tickerHome.css";
import "./rbAccount.css";

/** The hero photograph: the owner's pick, the golden-hour pen from the mock
 *  (sent 2026-10-25). The cattle sit on the right; the type sits on the dark
 *  left over a navy gradient. */
export const HERO_PHOTO = "/brand/membership-hero.jpg";

const CTA = "Start My Free 30 Days";
const NO_CARD = "No credit card required. Cancel anytime.";

/** `tool`: set by RequireAccount when this is the wall at a gated tool. */
export default function MembershipPage({ tool }: { tool?: string } = {}) {
  const { access } = useSiteAccess();
  const proof = useMemberProof();
  const stranger = access.state === "signup";
  // The one sign-up page; from a gated tool's wall, back to the tool after.
  const joinHref = tool && typeof window !== "undefined" ? `/join?next=${encodeURIComponent(window.location.pathname)}` : "/join";
  return (
    <TickerShell noIndexStrip>
      <div className="rbmp">
        <Hero proof={proof} tool={tool} stranger={stranger} joinHref={joinHref} />
        <StatBand proof={proof} />
        <Included proof={proof} />
        <section className="rbmp-plan" aria-label="The plan">
          {access.state === "loading" ? null
            : stranger ? <PriceCard joinHref={joinHref} />
            : <div className="rbacct-card" data-wall="membership"><MemberCard state={access.state} daysLeft={access.state === "open" ? access.daysLeft : null} /></div>}
        </section>
      </div>
    </TickerShell>
  );
}

function Hero({ proof, tool, stranger, joinHref }: { proof: PaywallProof | null; tool?: string; stranger: boolean; joinHref: string }) {
  return (
    <section className="rbmp-hero" style={{ backgroundImage: `url(${HERO_PHOTO})` }}>
      <div className="rbmp-hero-in">
        <p className="rbmp-eyebrow">{tool ? `${tool} is part of Membership` : "RaisedBeef Membership"}</p>
        <h1 className="rbmp-h1">The Entire<br />Cattle Market.<br /><em>One Membership.</em></h1>
        <p className="rbmp-sub">
          Live prices from <b>{barnCountLabel(proof?.barnCount ?? null)}</b> sale barns. {SITE_PLAN.indexCount} market indexes. Every tool you need to stay ahead.
        </p>
        {stranger && (
          <>
            <a className="rbmp-cta" href={joinHref}>{CTA} <span aria-hidden="true">→</span></a>
            <p className="rbmp-fine">{NO_CARD}</p>
          </>
        )}
      </div>
    </section>
  );
}

// The lower 48 as one outline, for the "Nationwide coverage" mark (owner, 2026-11-03: "better
// image of the united states" — the hand-drawn blob read as a potato). Printed by
// scripts/gen-us-icon.mjs from the same topology as the price maps; regenerate there, not by hand.
const US_OUTLINE =
  "M38.8,22.9L38.9,23.4L39.1,23.8L39.3,24.2L39.6,24.6L39.8,25L40.1,25.3L40.1,25.7L40.4,26.1L40.6,26.5L40.8,26.9L40.9,27.3L40.9,27.7L41,28.2L40.8,28.5L40.9,28.9L40.7,29.2L40.4,29L40,29L39.9,28.6L39.6,28.4L39.2,28.2L39,27.8L38.7,27.6L38.8,27.2L38.6,27.5L38.4,27.2L38.1,26.8L38.3,26.4L38,26.1L38.1,26.5L37.9,26.2L37.9,25.8L37.9,25.4L37.8,25L37.4,24.8L37.1,24.6L36.9,24.3L36.6,24.1L36.3,23.9L36,24.1L35.6,24.5L35.2,24.6L35,24.3L34.7,24.1L34.3,23.9L33.8,23.9L33.2,24L32.8,24.2L32.4,24.2L32.5,23.8L32.3,24.1L31.9,24.1L31.5,24.1L31.1,24.2L30.8,24.4L30.6,24.7L31,24.5L31.2,24.8L30.9,25L31.1,25.3L31.4,25.5L31.2,25.8L31,25.5L30.6,25.4L30.3,25.7L30.1,25.4L29.8,25.7L29.4,25.6L29.1,25.4L28.9,25.1L28.6,24.9L28.3,25.1L27.9,25.2L27.5,25.1L27,25L26.7,25.2L25.9,25.4L25.7,25.1L25.5,25.4L25.4,25.8L25.7,25.6L25.4,25.8L25.1,26.1L24.8,26.3L24.3,26.5L23.9,26.8L23.6,27.1L23.3,27.6L23.1,27.9L23.1,28.3L23.2,28.7L23.3,29.3L23.2,28.9L23.1,28.5L23.1,28.1L23.2,27.7L23.4,27.3L23.6,27L23.9,26.8L23.6,27L23.3,27.3L23.1,27.8L23,28.2L23,28.7L23.2,29L23.3,29.4L22.9,29.5L22.6,29.3L22.2,29.3L21.9,29.1L21.6,28.9L21.3,28.7L21.1,28.3L21,27.9L20.9,27.5L20.6,27.3L20.4,26.9L20.2,26.6L20,26.3L19.8,25.9L19.6,25.5L19.4,25.2L19.1,24.9L18.7,24.9L18.3,24.8L17.9,24.9L17.7,25.2L17.5,25.5L17.2,25.7L16.9,25.5L16.5,25.3L16.3,25L16,24.8L15.9,24.4L15.8,24L15.7,23.6L15.4,23.4L15.1,23.1L14.9,22.8L14.6,22.6L14.4,22.2L13.6,22.1L12.7,22L12.6,22.5L11.8,22.4L10.9,22.3L10.5,22.2L9.9,22.1L8.7,21.4L8,21L6.5,20.2L6.7,19.9L5.3,19.7L4.4,19.6L4.3,19.2L4.2,18.7L4,18.4L3.8,18.1L3.5,17.9L3.3,17.6L2.9,17.4L2.7,17.1L2.4,16.9L2,16.8L1.7,16.6L1.8,16.2L1.7,15.8L1.6,15.4L1.4,15.1L1.3,14.7L1.1,14.4L1.2,14L1.3,13.6L1,13.3L1,12.9L1,12.5L1.2,12.8L1.2,12.4L0.8,12.2L0.8,11.8L0.6,11.5L0.4,11L0.4,10.6L0.5,10.2L0.5,9.8L0.3,9.3L0.4,8.9L0.7,8.6L0.9,8.1L0.9,7.7L0.9,7.3L1,6.9L1,6.5L1.2,6.2L1.4,5.9L1.6,5.6L1.8,5.1L1.9,4.7L2.1,4.3L2.3,3.9L2.4,3.5L2.5,3.1L2.8,2.9L2.6,2.6L2.9,2.3L2.8,1.9L2.7,1.5L2.8,1.1L2.7,0.7L2.8,0.3L3.1,0.6L3.4,0.8L3.8,0.9L4.1,1.1L4,1.5L3.7,1.7L4,1.5L4.1,1.9L3.8,2.1L4.2,2L4.2,1.6L4.4,1.3L4.3,0.9L4.5,0.6L4.4,0.2L4.9,0.2L5.5,0.4L5.9,0.5L6.4,0.6L6.9,0.7L7.3,0.8L7.8,1L8.6,1.2L9.1,1.3L10,1.5L10.6,1.6L11.1,1.7L11.6,1.8L12.6,1.9L13.1,2L13.8,2.1L14.7,2.2L15.8,2.4L16.4,2.5L16.8,2.5L17.6,2.6L18.4,2.7L18.9,2.7L19.8,2.8L20.4,2.8L21.1,2.8L21.6,2.9L22.3,2.9L23,2.9L23.6,2.9L24.5,2.9L25.2,2.9L25.2,2.5L25.5,2.8L25.5,3.2L25.9,3.3L26.2,3.5L26.5,3.3L27,3.4L27.3,3.8L27.6,3.6L27.8,3.9L28.2,3.9L28.5,3.7L28.9,3.8L29.3,3.9L29,4.2L28.6,4.4L28.3,4.6L28.1,4.9L27.8,5.2L28.1,5.4L28.4,5.2L28.5,5.6L28.9,5.5L29.3,5.3L29.7,5.2L30,5L30.3,4.7L30.6,4.4L31,4.4L30.7,4.6L30.5,4.9L30.9,5L31.2,5.3L31.6,5.4L32,5.4L32.3,5.2L32.7,5.1L33.1,5L33.3,5.3L33.7,5.2L33.9,5.5L34.2,5.7L33.8,5.8L33.5,6L33.1,5.8L32.7,5.9L32.3,6L32.1,6.3L31.7,6.3L31.5,6.7L31.3,7L31.1,7.3L31,7.7L31.3,7.4L31.6,7.1L31.8,6.8L31.6,7.2L31.5,7.6L31.4,8L31.3,8.5L31.3,9L31.3,9.4L31.4,9.8L31.4,10.2L31.6,10.6L31.7,11L32.1,11L32.4,10.8L32.5,10.4L32.7,10.1L32.7,9.7L32.6,9.2L32.4,8.9L32.4,8.5L32.4,8.1L32.5,7.7L32.6,7.3L32.8,7L33,7.3L33.1,6.9L33.3,6.6L33.3,6.2L33.7,6.1L34,6.3L34.4,6.4L34.7,6.6L34.8,7L34.8,7.4L34.7,7.8L34.5,8.1L34.7,8.5L34.8,8.1L35.1,7.9L35.5,8L35.6,8.5L35.7,8.9L35.8,9.3L35.5,9.5L35.3,9.9L35.3,10.3L35.5,10.6L35.9,10.8L36.4,10.6L36.8,10.5L37,10.2L37.3,10L37.7,9.8L38,9.6L38.4,9.3L38.6,9L38.8,8.7L38.5,8.4L38.7,8.1L39.1,8L39.5,8L39.9,8L40.3,7.9L40.5,7.6L40.8,7.4L40.6,7.1L40.5,6.7L40.8,6.5L41,6.2L41.2,5.9L41.4,5.6L41.9,5.5L42.4,5.4L42.9,5.2L43.3,5.1L43.7,5.1L44.1,4.9L44.3,4.6L44.6,4.4L44.7,4L44.8,3.6L44.8,3.2L44.8,2.8L45.1,1.8L45.5,2L45.8,1.8L46.2,1.8L46.5,2.4L46.7,3L46.8,3.4L47.1,3.6L47.3,3.9L47.7,4L47.6,4.4L47.3,4.6L47.1,4.9L46.8,5.1L46.4,5.1L46.3,5.5L46.1,5.8L45.8,6L45.6,6.3L45.4,6.6L45.3,7.1L45.5,7.4L45.3,7.7L45.6,7.9L45.8,8.2L46.2,8.3L46.2,7.9L46.4,8.2L46.1,8.4L45.8,8.7L45.4,8.8L45.2,8.5L45.2,8.9L44.9,9.1L44.5,9.3L44.1,9.4L43.8,9.6L43.5,9.9L43.9,9.9L44.3,9.7L44.6,9.4L45,9.4L44.6,9.7L44.3,10L44,10.2L43.5,10.3L43.2,10.5L43.4,10.9L43.5,11.3L43.4,11.7L43.2,12.1L43,12.4L42.8,12.1L42.4,12L42.5,12.4L42.8,12.6L43,13L43,13.4L42.8,13.7L42.7,14.1L42.6,14.5L42.4,14.2L42.6,13.9L42.4,13.6L42.2,13.3L41.9,13.1L41.9,12.7L41.8,12.3L41.9,11.9L41.7,12.2L41.5,12.5L41.7,12.8L41.7,13.2L41.9,13.5L41.5,13.6L41.2,13.4L41.5,13.7L41.9,13.8L42.1,14.1L41.7,14L42,14.2L42.2,14.5L42.3,14.9L41.9,14.8L42.2,15L42.6,15L42.8,15.3L43,15.7L43.2,16.1L42.9,15.7L42.7,15.4L42.9,15.7L42.5,15.7L42.3,16L42.7,16L43.1,16.1L43,16.5L42.7,16.7L42.3,16.6L42,16.8L42.5,16.8L42.3,17.3L42.6,17.1L42.6,17.5L42.8,17.2L42.6,17.6L42.1,17.7L41.8,18L41.5,18.4L41.5,18.8L41.1,18.8L40.7,19L40.5,19.3L40.5,19.7L40.3,20L40,20.2L39.8,20.5L39.5,20.7L39.3,21L39.1,21.3L38.9,21.6L38.8,22.1L38.8,22.5Z";

function StatBand({ proof }: { proof: PaywallProof | null }) {
  return (
    <section className="rbmp-stats" aria-label="By the numbers">
      <div className="rbmp-stats-in">
        <div><b>{barnCountLabel(proof?.barnCount ?? null)}</b><span>Sale barns<br />live prices</span></div>
        <div><b>{SITE_PLAN.indexCount}</b><span>Market<br />indexes</span></div>
        <div><i aria-hidden="true"><svg viewBox="0 0 24 24" className="rbmp-fill"><rect x="3" y="13" width="5" height="8" rx="1" /><rect x="10" y="8" width="5" height="13" rx="1" /><rect x="17" y="3" width="5" height="18" rx="1" /></svg></i><span>Powerful<br />market tools</span></div>
        <div><i aria-hidden="true"><svg viewBox="0 0 48 30" className="rbmp-map rbmp-fill"><path d={US_OUTLINE} /></svg></i><span>Nationwide<br />coverage</span></div>
      </div>
    </section>
  );
}

/** The four columns under "One plan. All the tools." The first says the one
 *  thing nobody else has (owner, 2026-10-25). */
function Included({ proof }: { proof: PaywallProof | null }) {
  const barns = barnCountLabel(proof?.barnCount ?? null);
  const items = [
    { t: "Live Prices", d: `From ${barns} sale barns across the country`, i: <path d="M3 17l5-6 4 4 8-9M16 6h4v4" /> },
    { t: "Market Indexes", d: `${SITE_PLAN.indexCount} indexes with historical data`, i: <path d="M6 3h9l4 4v14H6zM14 3v5h5M9 13h6M9 17h6" /> },
    { t: "Tools & Trackers", d: "Weight maps, leaderboards, trackers and more", i: <path d="M12 21s-6-5.2-6-10a6 6 0 0 1 12 0c0 4.8-6 10-6 10zM12 13a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" /> },
    { t: "Reports & Alerts", d: "Sale reports, toplists and market insights", i: <g className="rbmp-fill"><rect x="3" y="13" width="5" height="8" rx="1" /><rect x="10" y="8" width="5" height="13" rx="1" /><rect x="17" y="3" width="5" height="18" rx="1" /></g> },
  ];
  return (
    <section className="rbmp-inc" aria-label="What is included">
      <div className="rbmp-inc-in">
        <p className="rbmp-kicker">Everything included</p>
        <h2 className="rbmp-h2">One plan. All the tools.</h2>
        <div className="rbmp-grid">
          {items.map((x) => (
            <div key={x.t} className="rbmp-item">
              <i aria-hidden="true"><svg viewBox="0 0 24 24">{x.i}</svg></i>
              <div><b>{x.t}</b><span>{x.d}</span></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Signed out: the price — the chip, the month or the year by the selector,
 *  the button, the no-card line. */
function PriceCard({ joinHref }: { joinHref: string }) {
  const [annual, setAnnual] = useState(false);
  return (
    <div className="rbmp-price">
      <span className="rbmp-chip">30 days free</span>
      <p className="rbmp-amt"><b>${annual ? SITE_PLAN.annual : SITE_PLAN.monthly}</b><small>/{annual ? "year" : "month"}</small></p>
      <div className="rbacct-period" role="group" aria-label="Billing period">
        <button type="button" aria-pressed={!annual} data-on={!annual ? "1" : undefined} onClick={() => setAnnual(false)}>Monthly</button>
        <button type="button" aria-pressed={annual} data-on={annual ? "1" : undefined} onClick={() => setAnnual(true)}>Yearly <em>1 mo free</em></button>
      </div>
      <a className="rbmp-cta" href={joinHref}>{CTA} <span aria-hidden="true">→</span></a>
      <p className="rbmp-fine">{NO_CARD}</p>
    </div>
  );
}

/** Signed in: the account page's own billing card, under the right heading. */
function MemberCard({ state, daysLeft }: { state: "open" | "expired"; daysLeft: number | null }) {
  const member = state === "open" && daysLeft === null;
  const line = state === "open" ? trialLine(daysLeft) : null;
  return (
    <>
      <p className="rbacct-eyebrow">{member ? `You're a ${SITE_PLAN.short}` : state === "expired" ? "Your free month has ended" : line ?? "Your free month"}</p>
      <h2 className="rbacct-title">{member ? "Everything is open to you" : "Subscribe to keep going"}</h2>
      <SiteBilling onWall />
      <p className="rbacct-fine">The homepage and Boxed Beef stay free. <a href="/account">Your account</a></p>
    </>
  );
}
